import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Field,
  FieldArray,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import {
  emailValidator,
  emailWOValueValidator,
  gstWOValueValidator,
  MobileNoValidator,
  MobileNoWOValueValidator,
  panWOValueValidator,
  passportWOValueValidator,
  pincodeWOValueValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import RippleButton from "../../components/common/RippleButton";
import ShadowCard from "../../components/common/ShadowCard";
import { getContactByID, updateContact } from "./services/contact.services";
import { clearContactDetails } from "./contactSlice";
import ButtonForAll from "../../components/common/ButtonForAll";
import { getAllActiveLeadSource } from "../leadsource/services/leadsource.service";
import { ILeadSource } from "../leadsource/leadsourceModel";
import FormDatePicker from "../../components/formFields/FormDateField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import {
  CONTACTTYPE,
  CUSTOMERCATEGORY,
  MARITALSTATUS,
  NAMEPREFIX,
  TagStars,
} from "../../_contstants/common";
import { findIndex } from "lodash";
import { ICity } from "../city/cityModel";
import { getAllActiveCities } from "../city/services/city.services";
import { getAllActiveStates } from "../state/services/state.services";
import { getAllActiveCountries } from "../country/services/country.services";
import { ICountry } from "../country/countryModel";
import { IState } from "../state/stateModel";
import { getAllActiveFoodOptions } from "../foodoption/services/foodOption.services";
import { IFoodOption } from "../foodoption/foodOptionModel";
import FormMultiSelectionFiled from "../../components/formFields/FormMultiSelectionFiled";
import VisaCountryArray from "./VisaCountryArray";
import moment from "moment";

interface CityChangeWatcherProps {
  formRenderProps: FormRenderProps;
  CityList: ICity[];
}

const CityChangeWatcher: React.FC<CityChangeWatcherProps> = ({
  CityList,
  formRenderProps,
}) => {
  const cityID = formRenderProps.valueGetter("CityID");

  useEffect(() => {
    const CityID = cityID ? cityID : undefined;
    const cityIndex = findIndex(CityList, { ID: CityID });
    if (cityIndex > -1) {
      const city = CityList[cityIndex];
      formRenderProps.onChange("StateID", { value: city.StateID });
      formRenderProps.onChange("CountryID", { value: city.CountryID });
    } else {
      formRenderProps.onChange("CityID", { value: "" });
      formRenderProps.onChange("StateID", { value: "" });
      formRenderProps.onChange("CountryID", { value: "" });
    }
  }, [cityID]);
  return null;
};

interface ChangeWatcherProps {
  formRenderProps: FormRenderProps;
}

const MaritalStatusChangeWatcher: React.FC<ChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const MaritalStatus = formRenderProps.valueGetter("MaritalStatus");

  useEffect(() => {
    if (MaritalStatus !== "Married") {
      formRenderProps.onChange("MarriageAnniversary", { value: "" });
      formRenderProps.onChange("NoofKids", { value: null });
    }
  }, [MaritalStatus]);
  return null;
};

const EditContact: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const ContactID = location.state?.ContactID;
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();

  const loading = useAppSelector((state) => state.contact.loading);
  const ContactDetails = useAppSelector(
    (state) => state.contact.ContactDetails
  );
  const LeadSourceList = useAppSelector(
    (state) => state.leadsource.LeadSourceList
  );
  const FoodOptionList = useAppSelector(
    (state) => state.foodOption.FoodOptionList
  );
  const CityList = useAppSelector((state) => state.city.CityList);
  const StateList = useAppSelector((state) => state.state.StateList);
  const CountryList = useAppSelector((state) => state.country.CountryList);

  const [formKey, setFormKey] = useState(1);
  const [initialValues, setInitialValues] = useState({});

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [initialValues]);

  useEffect(() => {
    setInitialValues(ContactDetails);
  }, [ContactDetails]);

  useEffect(() => {
    if (ContactID) {
      dispatch(getContactByID(ContactID));
    }
    return () => {
      dispatch(clearContactDetails());
    };
  }, [ContactID]);

  useEffect(() => {
    dispatch(getAllActiveCities());
    dispatch(getAllActiveStates());
    dispatch(getAllActiveCountries());
    dispatch(getAllActiveLeadSource());
    dispatch(getAllActiveFoodOptions());
  }, []);

  const handleSubmit = async (values: any) => {
    if (ContactID) {
      const Payload = {
        ID: ContactID ? +ContactID : null,
        Initial: values?.Initial ? values?.Initial : "",
        FirstName: values?.FirstName ? values?.FirstName : "",
        LastName: values?.LastName ? values?.LastName : "",
        MobileNo: values?.MobileNo ? values?.MobileNo : "",
        ContactType: values?.ContactType ? values?.ContactType : "",
        CountryCode: values?.CountryCode ? values?.CountryCode : "",
        AlternateMobileNo: values?.AlternateMobileNo
          ? values?.AlternateMobileNo
          : "",
        EmailID: values?.EmailID ? values?.EmailID : "",
        AlternateEmailID: values?.AlternateEmailID
          ? values?.AlternateEmailID
          : "",
        LeadSourceID: values?.LeadSourceID ? +values?.LeadSourceID : null,
        CountryID: values?.CountryID ? +values?.CountryID : null,
        StateID: values?.StateID ? +values?.StateID : null,
        CityID: values?.CityID ? +values?.CityID : null,
        Pincode: values?.Pincode ? `${values?.Pincode}` : "",
        Address: values?.Address ? values?.Address : "",
        DateofBirth: values?.DateofBirth
          ? moment(values?.DateofBirth)?.format("YYYY-MM-DD")
          : null,
        MaritalStatus: values?.MaritalStatus ? values?.MaritalStatus : "",
        MarriageAnniversary: values?.MarriageAnniversary
          ? moment(values?.MarriageAnniversary)?.format("YYYY-MM-DD")
          : null,
        NoofKids: values?.NoofKids ? +values?.NoofKids : null,
        FoodPreferenceID: values?.FoodPreferenceID
          ? +values?.FoodPreferenceID
          : null,
        PassportNumber: values?.PassportNumber ? values?.PassportNumber : "",
        PassportExpiry: values?.PassportExpiry
          ? moment(values?.PassportExpiry)?.format("YYYY-MM-DD")
          : null,
        CustomerCategory: values?.CustomerCategory
          ? `${values?.CustomerCategory}`
          : "",
        CompanyName: values?.CompanyName ? values?.CompanyName : "",
        GSTNumber: values?.GSTNumber ? values?.GSTNumber : "",
        Pancard: values?.Pancard ? values?.Pancard : "",
        Tags: Array.isArray(values.Tags)
          ? values.Tags.join(",")
          : values.Tags
          ? `${values.Tags}`
          : "",
        Remarks: values?.Remarks ? values?.Remarks : "",
        ContactVisa: values?.ContactVisa
          ? values?.ContactVisa?.map((visa: any) => {
              return {
                VisaCountryID: visa?.VisaCountryID
                  ? +visa?.VisaCountryID
                  : null,
                VisaExpiryDate: visa?.VisaExpiryDate
                  ? moment(visa?.VisaExpiryDate)?.format("YYYY-MM-DD")
                  : null,
              };
            })
          : [],
      };
      try {
        const response = await dispatch(updateContact(Payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(clearContactDetails());
          navigate("/contact");
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4 className="typography-head">
                    {ContactID ? "Update Contact" : "Add Contact"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem className="d-flex gap-2" colSpan={2}>
                  <Field
                    wrapperStyle={{
                      width: "100%",
                      flex: 2,
                    }}
                    id={"Initial"}
                    name={"Initial"}
                    label={"Initial"}
                    placeholder="i.e. Mr."
                    component={FormSelectionField}
                    options={NAMEPREFIX?.map((initial: any) => {
                      return {
                        value: initial?.value,
                        label: initial?.label,
                      };
                    })}
                  />
                  <Field
                    wrapperStyle={{
                      width: "100%",
                      flex: 3,
                    }}
                    name="FirstName"
                    label="First Name"
                    placeholder="i.e.John"
                    maxLength="100"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                  {/* </GridLayoutItem>
                <GridLayoutItem> */}
                  <Field
                    wrapperStyle={{
                      width: "100%",
                      flex: 3,
                    }}
                    name="LastName"
                    label="Last Name"
                    placeholder="i.e. Smith"
                    maxLength="100"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                  <Field
                    wrapperStyle={{
                      width: "100%",
                      flex: 3,
                    }}
                    name={"ContactType"}
                    label={"Contact Type"}
                    placeholder="i.e. B2B"
                    component={FormSelectionField}
                    options={CONTACTTYPE?.map((initial: any) => {
                      return {
                        value: initial?.value,
                        label: initial?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                // className="d-flex gap-2"
                >
                  {/* <Field
                    wrapperStyle={{
                      width: "100%",
                      flex: 1,
                    }}
                    name="CountryCode"
                    label="Code"
                    astrike={true}
                    placeholder="i.e. In"
                    component={CountryCodeField}
                    validator={requiredValidator}
                  /> */}
                  <Field
                    // wrapperStyle={{
                    //   width: "100%",
                    //   flex: 2,
                    // }}
                    name="MobileNo"
                    label="Mobile No."
                    // type="number"
                    // max={16}
                    type="text"
                    maxLength={16}
                    onKeyPress={(e: any) => {
                      const allowedChars = /^[0-9+]$/;
                      if (!allowedChars.test(e?.key)) {
                        e.preventDefault();
                      }
                    }}
                    placeholder="i.e. 9999999999"
                    component={FormTextField}
                    astrike={true}
                    validator={MobileNoValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="AlternateMobileNo"
                    label="Alternate Mobile No"
                    type="number"
                    placeholder="i.e. 9999999999"
                    max={10}
                    component={FormTextField}
                    validator={MobileNoWOValueValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="EmailID"
                    label="Email ID"
                    astrike={true}
                    placeholder="i.e. example@gmail.com"
                    component={FormTextField}
                    validator={emailValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="AlternateEmailID"
                    label="Alternate Email ID"
                    placeholder="i.e. example@gmail.com"
                    component={FormTextField}
                    validator={emailWOValueValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem className="mt-4" colSpan={3}>
                  <hr />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Address"
                    label="Address"
                    placeholder="Address"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="Pincode"
                    label="Pincode"
                    type="number"
                    component={FormTextField}
                    placeholder="Pincode"
                    minNumber="0"
                    max="6"
                    validator={pincodeWOValueValidator}
                    // validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"CityID"}
                    name={"CityID"}
                    label={"City"}
                    placeholder="i.e. Ahmedabad"
                    component={FormSelectionField}
                    isAddNew={true}
                    isIndirect={true}
                    drawerName="cityForm"
                    options={CityList?.map((city: ICity) => {
                      return {
                        value: city?.ID,
                        label: city?.CityName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"StateID"}
                    name={"StateID"}
                    label={"State"}
                    placeholder="i.e. Gujarat"
                    component={FormSelectionField}
                    disabled={true}
                    options={StateList?.map((state: IState) => {
                      return {
                        value: state?.ID,
                        label: state?.StateName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"CountryID"}
                    name={"CountryID"}
                    label={"Country"}
                    placeholder="i.e. India"
                    component={FormSelectionField}
                    disabled={true}
                    options={CountryList?.map((country: ICountry) => {
                      return {
                        value: country?.ID,
                        label: country?.CountryName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem className="mt-4" colSpan={3}>
                  <hr />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={"DateofBirth"}
                    label="Date Of Birth"
                    format="dd/MM/yyyy"
                    component={FormDatePicker}
                    maxDate={moment().toDate()}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="PassportNumber"
                    label="Passport Number"
                    placeholder="Passport Number"
                    component={FormTextField}
                    validator={passportWOValueValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={"PassportExpiry"}
                    label="Passport Expiry Date"
                    format="dd/MM/yyyy"
                    component={FormDatePicker}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"MaritalStatus"}
                    name={"MaritalStatus"}
                    label={"Marital Status"}
                    placeholder="i.e. Married"
                    component={FormSelectionField}
                    options={MARITALSTATUS?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={"MarriageAnniversary"}
                    label="Marriage Date"
                    format="dd/MM/yyyy"
                    disabled={
                      formRenderProps.valueGetter("MaritalStatus") !== "Married"
                    }
                    component={FormDatePicker}
                    maxDate={moment().toDate()}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name="NoofKids"
                    label="No Of Kids"
                    placeholder="0"
                    max={50}
                    format="n0"
                    disabled={
                      formRenderProps.valueGetter("MaritalStatus") !== "Married"
                    }
                    component={FormNumericTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"FoodPreferenceID"}
                    name={"FoodPreferenceID"}
                    label={"Food Preference"}
                    placeholder="i.e. Regular"
                    component={FormSelectionField}
                    isAddNew={true}
                    isIndirect={true}
                    drawerName="foodoptionForm"
                    options={FoodOptionList?.map((foodoption: IFoodOption) => {
                      return {
                        value: foodoption?.ID,
                        label: foodoption?.Name,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="w-100"
                    id={"LeadSourceID"}
                    name={"LeadSourceID"}
                    label={"Lead Source"}
                    placeholder="Lead Source"
                    component={FormSelectionField}
                    astrike={true}
                    validator={requiredValidator}
                    isAddNew={true}
                    isIndirect={true}
                    drawerName="leadsourceForm"
                    options={LeadSourceList?.map((leadsource: ILeadSource) => {
                      return {
                        value: leadsource?.ID,
                        label: leadsource?.Source,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}></GridLayoutItem>
                <GridLayoutItem className="mt-4" colSpan={3}>
                  <hr />
                </GridLayoutItem>
                <GridLayoutItem colSpan={3}>
                  <FieldArray
                    formRenderProps={formRenderProps}
                    component={VisaCountryArray}
                    name="ContactVisa"
                  />
                </GridLayoutItem>
                {/* <GridLayoutItem>
                  <Field
                    id={"VisaCoutryID"}
                    name={"VisaCoutryID"}
                    label={"Visa Country"}
                    placeholder="i.e. Canada"
                    component={FormSelectionField}
                    disabled={true}
                    options={CountryList?.map((country: ICountry) => {
                      return {
                        value: country?.ID,
                        label: country?.CountryName,
                      };
                    })}
                  />
                  <Field
                    name={"VisaExpiryDate"}
                    label="Visa Expiry Date"
                    format="dd/MM/yyyy"
                    component={FormDatePicker}
                  />
                </GridLayoutItem> */}
                <GridLayoutItem className="mt-4" colSpan={3}>
                  <hr />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"CustomerCategory"}
                    name={"CustomerCategory"}
                    label={"Customer Category"}
                    placeholder="i.e. Platinum"
                    component={FormSelectionField}
                    options={CUSTOMERCATEGORY?.map((category: any) => {
                      return {
                        value: category?.value,
                        label: category?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="CompanyName"
                    label="Company Name"
                    placeholder="Company Name"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="GSTNumber"
                    label="GST No."
                    placeholder="GST No."
                    validator={gstWOValueValidator}
                    component={FormTextField}
                    maxLength={15}
                    onChange={(e) =>
                      formRenderProps.onChange("GSTNo", {
                        value: e?.value?.toUpperCase(),
                      })
                    }
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="Pancard"
                    label="PAN No."
                    placeholder="PAN No."
                    maxLength={10}
                    validator={panWOValueValidator}
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"Tags"}
                    name={"Tags"}
                    label={"Tags"}
                    placeholder="i.e. Platinum"
                    component={FormMultiSelectionFiled}
                    options={TagStars?.map((tags: any) => {
                      return {
                        value: tags?.value,
                        label: tags?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem></GridLayoutItem>
                <GridLayoutItem colSpan={3}>
                  <Field
                    name="Remarks"
                    label="Remarks"
                    placeholder="Remarks"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  colSpan={3}
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "end",
                    marginTop: 20,
                  }}
                >
                  <ButtonForAll
                    label={ContactID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                  />

                  <RippleButton
                    type="button"
                    fillMode="outline"
                    themeColor="primary"
                    style={{ marginLeft: 10 }}
                    onClick={() => navigate("/contact")}
                  >
                    Cancel
                  </RippleButton>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
            <CityChangeWatcher
              formRenderProps={formRenderProps}
              CityList={CityList}
            />
            <MaritalStatusChangeWatcher formRenderProps={formRenderProps} />
          </FormElement>
        )}
      />
    </>
  );
};

export default EditContact;
